import { useQuery, useQueries, useMutation } from "@tanstack/react-query";
import FirebaseDao, { QueryOptions } from "../Firebase/FirebaseDao";
import { OrderByDirection } from "firebase/firestore";

export const useGetDocumentByDocumentId = (
  collectionName: string,
  documentId: string
) => {
  return useQuery({
    queryKey: ["document", collectionName, documentId],
    queryFn: () => FirebaseDao.read(collectionName, documentId),
    refetchOnWindowFocus: false,
  });
};

export const useGetAllDocuments = (collectionName: string) => {
  return useQuery({
    queryKey: ["all", collectionName],
    queryFn: () => FirebaseDao.readAll(collectionName),
    refetchOnWindowFocus: false,
  });
};

export const useGetDocumentsByQuery = (
  collectionName: string,
  queryOptions: QueryOptions[],
  limitNum?: number,
  orderByField?: string,
  orderDirection?: OrderByDirection
) => {
  return useQuery({
    queryKey: ["query", collectionName, queryOptions],
    queryFn: () =>
      FirebaseDao.query(
        collectionName,
        queryOptions,
        limitNum,
        orderByField,
        orderDirection
      ),
    refetchOnWindowFocus: false,
  });
};

export const useCountByQuery = (
  collectionName: string,
  queryOptions: QueryOptions[]
) => {
  return useQuery({
    queryKey: ["count", collectionName, queryOptions],
    queryFn: () => FirebaseDao.count(collectionName, queryOptions),
    refetchOnWindowFocus: false,
  });
};

export const useCountByMultipleQueries = (
  collectionName: string,
  queryOptions: QueryOptions[][]
) => {
  return useQueries({
    queries: queryOptions.map((options) => ({
      queryKey: ["count", collectionName, options],
      queryFn: () => FirebaseDao.count(collectionName, options),
      refetchOnWindowFocus: false,
    })),
  });
};

export const useCreateDocument = () => {
  return useMutation({
    mutationKey: ["create"],
    mutationFn: ({
      collectionName,
      doc,
    }: {
      collectionName: string;
      doc: any;
    }) => FirebaseDao.create(collectionName, doc),
  });
};

export const useDeleteDocument = () => {
  return useMutation({
    mutationKey: ["delete"],
    mutationFn: ({
      collectionName,
      documentId,
    }: {
      collectionName: string;
      documentId: string;
    }) => FirebaseDao.delete(collectionName, documentId),
  });
};

export const useUpdateDocument = () => {
  return useMutation({
    mutationKey: ["update"],
    mutationFn: ({
      collectionName,
      documentId,
      data,
    }: {
      collectionName: string;
      documentId: string;
      data: any;
    }) => FirebaseDao.update(collectionName, documentId, data),
  });
};
